import {
  Dinner,
  DinnerCandidate,
  Image,
  User,
} from '~/api-definition/definition'
import { DinnerMemberType } from '~/api-definition/enumTypes'
import { config } from '~/config'

import { extractUrl } from './url'

export type MinimumDinnerUser = Pick<
  User,
  | 'userId'
  | 'userName'
  | 'displayName'
  | 'profileIcon'
  | 'rank'
  | 'showRankFrame'
  | 'unfavoriteFoods'
  | 'allergicFoods'
  | 'state'
>

export type MinimumDinnerMember = {
  user: MinimumDinnerUser
  memberType: DinnerMemberType
}

export type DinnerMemberFilter =
  | 'organized'
  | 'invited'
  | 'attended'
  | 'applied'

export const dinnerMainThumbnailImage = (dinner: Dinner) => {
  return {
    try:
      dinner.thumbnailImages[0]?.imageUrl ??
      dinner.restaurant?.thumbnailImages[0]?.imageUrl,
    fallback: config.noImageUrl.restaurantCoverImage,
  }
}

export const dinnerThumbnailImages = (dinner: Dinner, count: number) => {
  const images =
    (dinner.thumbnailImages.length > 0
      ? dinner.thumbnailImages
      : dinner.restaurant?.thumbnailImages
    )?.slice(0, count) ?? []
  return images.length === 0
    ? undefined
    : [...Array(count)].map((_, i) => images[i])
}

export const dinnerMainThumbnailImageOpt = (dinner: Dinner | undefined) => {
  if (dinner == null)
    return {
      try: config.noImageUrl.restaurantCoverImage,
      fallback: config.noImageUrl.restaurantCoverImage,
    }
  return dinnerMainThumbnailImage(dinner)
}

export const dinnerThumbnailImageToSrc = (dinnerThumbnailImage: Image) => {
  return {
    try: dinnerThumbnailImage.imageUrl,
    fallback: config.noImageUrl.restaurantCoverImage,
  }
}

export const dinnerThumbnailImageOptToSrc = (
  dinnerThumbnailImage: Image | undefined
) => {
  return {
    try: dinnerThumbnailImage?.imageUrl,
    fallback: config.noImageUrl.restaurantCoverImage,
  }
}

export const dinnerRestaurantId = (dinner: Dinner) => {
  return dinner.restaurant?.restaurantId
}

export const dinnerRestaurantName = (dinner: Dinner) => {
  return dinner.restaurant?.restaurantName ?? dinner.restaurantName
}

export const dinnerRestaurantNameOpt = (dinner: Dinner | undefined) => {
  if (dinner == null) return undefined
  return dinnerRestaurantName(dinner)
}

export const dinnerRestaurantAddress = (dinner: Dinner | undefined) => {
  if (dinner == null) return undefined
  return dinner.restaurantAddress ?? dinner.restaurant?.address
}

export const dinnerRestaurantMapUrl = (
  dinner: Dinner | undefined
): string | undefined => {
  if (dinner == null) return undefined
  const baseUrl = 'https://maps.google.com/maps?zoom=16'

  // Use dinner.restaurantAddress if available
  if (dinner.restaurantAddress) {
    const encodedAddress = encodeURIComponent(dinner.restaurantAddress)
    return `${baseUrl}&q=${encodedAddress}`
  }

  // Use restaurantName, latitude and longitude if available
  const { restaurant } = dinner
  if (restaurant && restaurant.latitude && restaurant.longitude) {
    const query = restaurant.restaurantName
      ? `${encodeURIComponent(restaurant.restaurantName)}@${
          restaurant.latitude
        },${restaurant.longitude}`
      : `${restaurant.latitude},${restaurant.longitude}`
    return `${baseUrl}&q=${query}`
  }

  // Return undefined if neither condition is met
  return undefined
}

export const dinnerRestaurantUrl = (dinner: Dinner) => {
  if (dinner.restaurantUrl) {
    return extractUrl(dinner.restaurantUrl)
  }
  if (dinner.restaurant?.tabelogRestaurantUrl) {
    return dinner.restaurant.tabelogRestaurantUrl
  }
  return ''
}

export const dinnerOrganizer = (dinner: Dinner) => {
  return dinner.organizerUser != null
    ? { user: dinner.organizerUser }
    : dinner.members.find((member) => member.memberType === 'organizer')
}

export const dinnerIsOrganized = (dinner: Dinner, userId: string) => {
  if (dinner.organizerUser?.userId === userId) return true

  const memberType = dinner.members.find(
    (member) => member.user.userId === userId
  )?.memberType
  return memberType === 'organizer' || memberType === 'co_organizer'
}

export const dinnerParticipationStatusText = (dinner: Dinner) => {
  if (dinner.participation?.memberType === 'organizer') return '幹事'
  if (dinner.participation?.memberType === 'co_organizer') return '共同幹事'

  if (dinner.participation?.memberState === 'applied') return '応募中'
  if (dinner.participation?.memberState === 'invited') return '誘われ中'
  if (dinner.participation?.memberState === 'active') return '参加確定'
}

export const dinnerOrganizerDisplayName = (dinner: Dinner) => {
  return dinnerOrganizer(dinner)?.user.displayName
}

export const dinnerOrganizerDisplayNameOpt = (dinner: Dinner | undefined) => {
  if (dinner == null) return undefined
  return dinnerOrganizerDisplayName(dinner)
}

export const dinnerBudget = (dinner: Dinner) => {
  if (dinner.fixedBudgetAmount || dinner.fixedBudgetAmount === 0) {
    return `¥ ${dinner.fixedBudgetAmount.toLocaleString()}`
  } else if (dinner.minBudgetAmount || dinner.maxBudgetAmount) {
    return `${dinner.minBudgetAmount?.toLocaleString('ja-JP', {
      style: 'currency',
      currencyDisplay: 'symbol',
      currency: 'JPY',
    })}～${dinner.maxBudgetAmount?.toLocaleString('ja-JP', {
      style: 'currency',
      currencyDisplay: 'symbol',
      currency: 'JPY',
    })}`
  } else {
    return ''
  }
}

export const dinnerIsRecruiting = (dinner: Dinner) => {
  return dinner.seatNumber > 0 && dinner.scheduledAt * 1000 > Date.now()
}

export const dinnerRecruitmentProgress = (
  dinner: Dinner,
  options?: { longVersion?: true }
) => {
  if (options?.longVersion) {
    if (dinnerIsRecruiting(dinner)) {
      return `${dinner.seatNumber}名募集中（${dinner.reservationNumber}名予約）`
    } else {
      return `募集終了（${dinner.reservationNumber}名予約）`
    }
  } else {
    return `${dinner.seatNumber}名/${dinner.reservationNumber}名${
      dinnerIsRecruiting(dinner) ? ' 現在募集中' : ' 募集終了'
    }`
  }
}

const dinnerGenre = (dinner: Dinner) => {
  if ((dinner.restaurant?.categories.length ?? 0) > 0) {
    return dinner.restaurant?.categories
      .map((category) => category.categoryName)
      .join('・')
  }
  return ''
}

const dinnerArea = (dinner: Dinner) => {
  if (dinner.restaurant != null) {
    return dinner.restaurant?.areaName != null &&
      dinner.restaurant?.areaName.length > 0
      ? dinner.restaurant?.areaName
      : dinner.restaurantAddress ?? ''
  } else {
    return ''
  }
}

export const dinnerAreaAndGenre = (dinner: Dinner): string => {
  const genre = dinnerGenre(dinner) ?? ''
  const area = dinnerArea(dinner) ?? ''
  if (genre.length > 0 && area.length > 0) {
    return genre + '/' + area
  } else if (genre.length > 0) {
    return genre
  } else if (area.length > 0) {
    return area
  } else {
    return ''
  }
}

export const dinnerAreaAndGenreOpt = (dinner: Dinner | undefined) => {
  if (dinner == null) return undefined
  return dinnerAreaAndGenre(dinner)
}

export const dinnerGroupCandidates = (dinnerCandidates: DinnerCandidate[]) => {
  const grouped: {
    rejected: DinnerCandidate[]
    invited: DinnerCandidate[]
    superWish: DinnerCandidate[]
    wish: DinnerCandidate[]
    normal: DinnerCandidate[]
  } = {
    rejected: [],
    invited: [],
    superWish: [],
    wish: [],
    normal: [],
  }
  dinnerCandidates.forEach((candidate) => {
    if (candidate.muted) {
      return
    }

    if (candidate.rejected) {
      grouped.rejected.push(candidate)
      return
    }
    if (candidate.invited) {
      grouped.invited.push(candidate)
      return
    }
    if (candidate.followingStatus.superWish) {
      grouped.superWish.push(candidate)
      return
    }
    if (candidate.followingStatus.wish) {
      grouped.wish.push(candidate)
      return
    }
    grouped.normal.push(candidate)
  })
  return grouped
}

export const dinnerMemberTypeLabels = {
  ['organizer']: '幹事',
  ['co_organizer']: '共同幹事',
  ['attendee']: '同席者',
}

// Google Calendar
function formatTimestampToISOString(timestamp: number): string {
  const date = new Date(timestamp * 1000)
  const YYYY = date.getUTCFullYear()
  const MM = String(date.getUTCMonth() + 1).padStart(2, '0') // Months are zero-based
  const DD = String(date.getUTCDate()).padStart(2, '0')
  const hh = String(date.getUTCHours()).padStart(2, '0')
  const mm = String(date.getUTCMinutes()).padStart(2, '0')
  const ss = String(date.getUTCSeconds()).padStart(2, '0')

  return `${YYYY}${MM}${DD}T${hh}${mm}${ss}Z`
}

export const dinnerGoogleCalendarUrl = (dinner: Dinner) => {
  const startDate = formatTimestampToISOString(dinner.scheduledAt)
  const endDate = formatTimestampToISOString(dinner.scheduledAt + 2 * 3600) // 2 hours later

  const restaurantName = dinnerRestaurantName(dinner)
  const restaurantAddress = dinnerRestaurantAddress(dinner)

  const baseUrl: string = 'https://www.google.com/calendar/render'

  const params: any = {
    action: 'TEMPLATE',
    trp: 'true',
    text:
      dinner.dinnerName.length > 0
        ? restaurantName + '「' + dinner.dinnerName + '」'
        : restaurantName,
    location: restaurantAddress,
    dates: `${startDate}/${endDate}`,
    details:
      restaurantName +
      '\n' +
      restaurantAddress +
      '\n' +
      config.baseUrl +
      '/dinners/' +
      dinner.dinnerId,
  }

  const encodedParams: string = Object.keys(params)
    .map((key: string) => `${key}=${encodeURIComponent(params[key])}`)
    .join('&')

  const url: string = `${baseUrl}?${encodedParams}`
  return url
}

// Outlook Calendar
function formatDateToISO(date: Date, utc: boolean = false): string {
  const year = utc ? date.getUTCFullYear() : date.getFullYear()
  const month = String(
    utc ? date.getUTCMonth() + 1 : date.getMonth() + 1
  ).padStart(2, '0')
  const day = String(utc ? date.getUTCDate() : date.getDate()).padStart(2, '0')
  const hours = String(utc ? date.getUTCHours() : date.getHours()).padStart(
    2,
    '0'
  )
  const minutes = String(
    utc ? date.getUTCMinutes() : date.getMinutes()
  ).padStart(2, '0')
  const seconds = String(
    utc ? date.getUTCSeconds() : date.getSeconds()
  ).padStart(2, '0')

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${
    utc ? 'Z' : ''
  }`
}

function formatTimestampToLocalISO(timestamp: number): string {
  return formatDateToISO(new Date(timestamp * 1000))
}

function formatTimestampToUTCISO(timestamp: number): string {
  return formatDateToISO(new Date(timestamp * 1000), true)
}

export const dinnerOutlookCalendarUrl = (
  dinner: Dinner,
  useNativeDeeplink: boolean
): string => {
  const startDate = useNativeDeeplink
    ? formatTimestampToUTCISO(dinner.scheduledAt)
    : formatTimestampToLocalISO(dinner.scheduledAt)
  const endDate = useNativeDeeplink
    ? formatTimestampToUTCISO(dinner.scheduledAt + 2 * 3600) // 2 hours later
    : formatTimestampToLocalISO(dinner.scheduledAt + 2 * 3600)

  const restaurantName = encodeURIComponent(dinnerRestaurantName(dinner) || '')
  const restaurantAddress = encodeURIComponent(
    dinnerRestaurantAddress(dinner) || ''
  )
  const details = encodeURIComponent(
    [
      dinnerRestaurantName(dinner),
      dinnerRestaurantAddress(dinner) || '',
      `${config.baseUrl}/dinners/${dinner.dinnerId}`,
    ].join('\n')
  )

  const baseUrl = useNativeDeeplink
    ? 'ms-outlook://events/new'
    : 'https://outlook.office.com/calendar/deeplink/compose'

  let params: Record<string, string> = {}

  if (useNativeDeeplink) {
    // For native deeplink
    params = {
      title:
        dinner.dinnerName.length > 0
          ? `${restaurantName}「${dinner.dinnerName}」`
          : restaurantName,
      description: details,
      start: startDate,
      end: endDate,
      location: restaurantAddress,
    }
  } else {
    // For web deeplink
    params = {
      subject:
        dinner.dinnerName.length > 0
          ? `${restaurantName}「${dinner.dinnerName}」`
          : restaurantName,
      body: details,
      startdt: startDate,
      enddt: endDate,
      location: restaurantAddress,
    }
  }

  // Generate the final URL
  const encodedParams: string = Object.keys(params)
    .map((key: string) => `${key}=${params[key]}`)
    .join('&')

  const url: string = `${baseUrl}?${encodedParams}`
  return url
}
